import axiosInstance from "./axios.config";

export interface YardSubmissionRequest {
    yardName: string;
    setupData: Record<string, unknown>;
    parkingData: Record<string, unknown>;
}

export interface YardSubmissionResponse {
    success: boolean;
    yardName: string;
    accountId: string;
    prefix: string;
    setupKey: string;
    parkingKey: string;
}

export const submitYard = async (
    data: YardSubmissionRequest
): Promise<YardSubmissionResponse> => {
    const response = await axiosInstance.post<YardSubmissionResponse>(
        "/yards/submit",
        data
    );

    return response.data;
};


/**
 * Retrieve the authenticated user's saved yard layout.
 */
export interface YardLayoutResponse {
    success: boolean;
    yardName: string;
    layout: import("../components/yard/YardCanvas").YardSetup;
    etag: string;
}

export const getYardLayout = async (): Promise<YardLayoutResponse> => {
    const response = await axiosInstance.get<YardLayoutResponse>(
        "/yards/layout"
    );

    return response.data;
};


/**
 * Upload the original aerial image to the authenticated yard.
 * The backend validates dimensions and prevents overwrites.
 */
export const uploadOriginalYardImage = async (
    image: Blob
): Promise<void> => {
    const formData = new FormData();

    formData.append(
        "file",
        image,
        image.type === "image/jpeg"
            ? "original_yard.jpg"
            : "original_yard.png"
    );

    await axiosInstance.post(
        "/yards/original-image",
        formData,
        {
            headers: {
                "Content-Type": undefined
            }
        }
    );
};


/**
 * Retrieve the original aerial image for the authenticated yard.
 * Returns a temporary browser object URL.
 */
export const getOriginalYardImage = async (): Promise<string> => {
    const response = await axiosInstance.get<Blob>(
        "/yards/original-image",
        {
            responseType: "blob"
        }
    );

    return URL.createObjectURL(response.data);
};


/**
 * Retrieve an original MissionCode image from the private S3 bucket.
 * The backend handles S3 access and authentication.
 */
export type YardImageName =
    | "RedHash.png"
    | "White Parking.png"
    | "YellowHash.png";

export const getYardImage = async (
    filename: YardImageName
): Promise<string> => {
    const response = await axiosInstance.get<Blob>(
        `/yards/assets/${encodeURIComponent(filename)}`,
        {
            responseType: "blob"
        }
    );

    return URL.createObjectURL(response.data);
};


/**
 * Retrieve the authenticated user's complete Python-rendered yard map.
 *
 * Returns a temporary browser object URL for the PNG.
 * The caller must revoke the URL when it is no longer needed.
 */
export const getRenderedYardImage = async (): Promise<string> => {
    const response = await axiosInstance.get<Blob>(
        "/yards/render",
        {
            responseType: "blob"
        }
    );

    return URL.createObjectURL(response.data);
};


/**
 * Retrieve live parking records for the authenticated yard.
 * Read-only; parking.json remains the source of truth.
 */
export interface YardParkingResponse {
    success: boolean;
    yardName: string;
    parking: {
        parking_spaces: Record<string, unknown>[];
        [key: string]: unknown;
    };
}

export const getParkingData = async (): Promise<YardParkingResponse> => {
    const response = await axiosInstance.get<YardParkingResponse>(
        "/yards/parking"
    );

    return response.data;
};


/**
 * Retrieve an approved trailer image through the authenticated backend.
 * Returns an object URL that the caller must revoke when finished.
 */
export type TrailerImageName =
    | "28 foot dry van.png"
    | "28 foot intermodal.png"
    | "28 foot reefer.png"
    | "53 foot dry van.png"
    | "53 foot intermodal.png"
    | "53 foot reefer.png";

export const getTrailerImage = async (
    filename: TrailerImageName
): Promise<string> => {
    const response = await axiosInstance.get<Blob>(
        `/yards/trailer-images/${encodeURIComponent(filename)}`,
        {
            responseType: "blob"
        }
    );

    return URL.createObjectURL(response.data);
};


/**
 * Save changes to an existing yard.
 * The backend preserves live parking data and the original aerial image.
 */
export const updateYard = async (
    setupData: Record<string, unknown>,
    etag: string
) => {
    const response = await axiosInstance.put(
        "/yards/update",
        {
            setupData,
            etag,
        }
    );

    return response.data;
};



/**
 * Permanently delete the authenticated administrator's assigned yard
 * setup.json and parking.json after explicit confirmation.
 */
export interface YardDeletionResponse {
    success: boolean;
    yardName: string;
    deletedKeys: string[];
}

export const deleteYard = async (
    yardName: string,
    confirmation: string
): Promise<YardDeletionResponse> => {
    const response = await axiosInstance.post<YardDeletionResponse>(
        "/yards/delete",
        {
            yardName,
            confirmation,
        }
    );

    return response.data;
};
